import { useIonAlert } from "@ionic/react";
import L10n from "localization";
import { useCallback, useState } from "react";

const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Allowable MIME types and (fallback) extensions
const ALLOWED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "application/pdf",
];
const ALLOWED_EXTENSIONS = [".png", ".jpeg", ".jpg", ".gif", ".webp", ".pdf"];

function isAllowedFileType(file: File): boolean {
  if (ALLOWED_MIME_TYPES.includes(file.type)) {
    return true;
  }
  // fallback: check extension case-insensitively
  const lower = file.name.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

interface IProps {
  onFilesSelected?: (files: File[]) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  children: React.ReactNode;
  multiple?: boolean;
  accept?: string;
}

export default function FileDropZone({ onFilesSelected, disabled, style, children }: IProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [presentAlert] = useIonAlert();

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Set dragging state to true only if a file is being dragged over
    if (e.dataTransfer.items?.[0]?.kind === "file") {
      setIsDragging(true);
    }
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDragging && e.dataTransfer.items?.[0]?.kind === "file") {
        setIsDragging(true);
      }
    },
    [isDragging],
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled) return;

      const files = Array.from(e.dataTransfer.files || []);
      const validFiles: File[] = [];
      for (const file of files) {
        if (!isAllowedFileType(file)) {
          presentAlert(
            L10n.localize((s) => s.funcs.general.fileTypeNotAllowed)(
              file.name,
              ALLOWED_EXTENSIONS.join(", "),
            ),
          );
          continue;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
          presentAlert(
            L10n.localize((s) => s.funcs.general.fileTooLarge)(
              file.name,
              (file.size / 1024 / 1024).toFixed(2),
              MAX_FILE_SIZE_MB,
            ),
          );
          continue;
        }
        validFiles.push(file);
      }
      if (validFiles.length && onFilesSelected) {
        onFilesSelected(validFiles);
      }
    },
    [onFilesSelected, disabled, presentAlert],
  );

  return (
    <div
      style={{
        ...style,
        backgroundColor: disabled ? "transparent" : isDragging ? "#f0f0f0" : "transparent",
        transition: "background 0.2s",
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
    </div>
  );
}
